// === Admin Portal Root Component ===

import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { AdminThemeProvider } from './lib/theme-context';
import { AuthProvider } from './components/AuthProvider';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { AdminDashboardPage } from './pages/admin-dashboard';
import { LoginPage } from './pages/login';
import { AuthCallbackPage } from './pages/auth-callback';
import { CategoriesPage } from './pages/categories';
import { DailyCheckListPage } from './pages/daily-check-list';
import { PreCheckPage } from './pages/pre-check';
import { PostCheckPage } from './pages/post-check';
import { PostcodesPage } from './pages/postcodes';
import { StatesPage } from './pages/states';
import { TimezonesPage } from './pages/timezones';
import { DatabaseMigrationsPage } from './pages/database-migrations';
import { CecsPage } from './pages/cecs';
import { CropTypesPage } from './pages/crop-types';
import { MlHoursPage } from './pages/ml-hours';
import { SurveillancePestsPage } from './pages/surveillance-pests';
import { PostHarvestChecksPage } from './pages/post-harvest-checks';
import { PreHarvestChecksTemplatesPage } from './pages/pre-harvest-checks-templates';
import { PpeEquipmentPage } from './pages/ppe-equipment';
import { ProgramSettingsListsPage } from './pages/program-settings-lists';
import { UserTitlesBackendsPage } from './pages/user-titles-backends';
import { SwmsRankingRiskBackendsPage } from './pages/swms-ranking-risk-backends';
import { SafetyDataSheetsPage } from './pages/safety-data-sheets';
import { FertilizerCompaniesPage } from './pages/fertilizer-companies';
import { ChemicalsPage } from './pages/chemicals';
import { FertilizersPage } from './pages/fertilizers';
import { PricelistTemplatesPage } from './pages/pricelist-templates';
import { UsersPage } from './pages/users';
import { DiscountCodesPage } from './pages/discount-codes';
import { PrivacyStatementsPage } from './pages/privacy-statements';
import { SwmsTasksBackendsPage } from './pages/swms-tasks-backends';
import { TaskTypesPage } from './pages/task-types';
import { RateHectaresPage } from './pages/rate-hectares';
import { DistributorsPage } from './pages/distributors';
import { SoilTestLabsPage } from './pages/soil-test-labs';
import { ChemicalDistributorAssignmentPage } from './pages/chemical-distributor-assignment';
import { AdvertisingsPage } from './pages/advertisings';
import { CompaniesPage } from './pages/companies';
import { ClientManagementPage } from './pages/client-management';
import { BugReportsPage } from './pages/bug-reports';
import { DocumentLinkCheckPage } from './pages/document-link-check';

// AdminThemeProvider uses useAuth(), so it must be nested inside AuthProvider.
// It wraps MuiThemeProvider internally, so no separate ThemeProvider is needed.

// Layout route: applies ProtectedRoute + AppLayout to all nested child routes.
// To add a new protected page, just add a <Route> inside the layout route below.
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </ProtectedRoute>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminThemeProvider>
          <CssBaseline />
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* All protected pages share the AppLayout topbar */}
            <Route element={<ProtectedLayout />}>
              <Route path="/dashboard" element={<AdminDashboardPage />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/daily-check-list" element={<DailyCheckListPage />} />
              <Route path="/pre-check" element={<PreCheckPage />} />
              <Route path="/post-check" element={<PostCheckPage />} />
              <Route path="/postcodes" element={<PostcodesPage />} />
              <Route path="/states" element={<StatesPage />} />
              <Route path="/timezones" element={<TimezonesPage />} />
              <Route path="/database-migrations" element={<DatabaseMigrationsPage />} />
              <Route path="/cecs" element={<CecsPage />} />
              <Route path="/crop-types" element={<CropTypesPage />} />
              <Route path="/ml-hours" element={<MlHoursPage />} />
              <Route path="/surveillance" element={<SurveillancePestsPage />} />
              <Route path="/post-harvest-checks" element={<PostHarvestChecksPage />} />
              <Route path="/pre-harvest-checks-templates" element={<PreHarvestChecksTemplatesPage />} />
              <Route path="/ppe-equipments" element={<PpeEquipmentPage />} />
              <Route path="/program-settings-lists" element={<ProgramSettingsListsPage />} />
              <Route path="/user-titles-backends" element={<UserTitlesBackendsPage />} />
              <Route path="/swms-ranking-risk-backends" element={<SwmsRankingRiskBackendsPage />} />
              <Route path="/safety-data-sheets" element={<SafetyDataSheetsPage />} />
              <Route path="/fertilizer-companies" element={<FertilizerCompaniesPage />} />
              <Route path="/chemicals" element={<ChemicalsPage />} />
              <Route path="/fertilizers" element={<FertilizersPage />} />
              <Route path="/pricelist-templates" element={<PricelistTemplatesPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/discount-codes" element={<DiscountCodesPage />} />
              <Route path="/privacy-statements" element={<PrivacyStatementsPage />} />
              <Route path="/swms-tasks-backends" element={<SwmsTasksBackendsPage />} />
              <Route path="/task-types" element={<TaskTypesPage />} />
              <Route path="/rate-hectares" element={<RateHectaresPage />} />
              <Route path="/distributors" element={<DistributorsPage />} />
              <Route path="/soil-test-labs" element={<SoilTestLabsPage />} />
              <Route path="/chemical-distributor-assignment" element={<ChemicalDistributorAssignmentPage />} />
              <Route path="/advertisings" element={<AdvertisingsPage />} />
              <Route path="/companies" element={<CompaniesPage />} />
              <Route path="/client-management" element={<ClientManagementPage />} />
              <Route path="/bug-reports" element={<BugReportsPage />} />
              <Route path="/document-link-check" element={<DocumentLinkCheckPage />} />
            </Route>
          </Routes>
        </AdminThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

